import { useCallback, useMemo, useState } from 'react';
import type {
	CanvasPlacement,
	PdfBox,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/PlacementCanvas.types';
import {
	buildSlotMetaMap,
	defaultBoxFor,
	isPlacementSetComplete,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/signaturePlacementShared';
import {
	buildCounterpartySlots,
	toPlacementPayload,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyPlacement.utils';
import type {
	CounterpartyPlacementResult,
	UseCounterpartyPlacementArgs,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/useCounterpartyPlacement.types';
import { findOverlappingKeys } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/placementCollision.utils';

/**
 * Canvas state for the boxes a manager draws when adding a counterparty to a live document.
 *
 * Simpler than its two siblings: `showStampMetadata` comes off the template and cannot be
 * toggled here, and no slot is ever a witness, so there is no rescale effect and one ratio
 * throughout. Slots are derived from the party name and slot count on every render, so stepping
 * back and changing either updates the panel without an effect writing state — a box for a slot
 * that no longer exists simply stops being read.
 */
export const useCounterpartyPlacement = ({
	partyName,
	slotCount,
	showStampMetadata,
	fixedPlacements,
}: UseCounterpartyPlacementArgs): CounterpartyPlacementResult => {
	const slots = useMemo(
		() => buildCounterpartySlots(partyName, slotCount),
		[partyName, slotCount]
	);
	const slotByKey = useMemo(() => buildSlotMetaMap(slots, (slot) => slot.key), [slots]);

	const [placementsByKey, setPlacementsByKey] = useState<Map<string, CanvasPlacement>>(new Map());
	const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);

	const onPlacementChange = useCallback((key: string, pageNum: number, pdfBox: PdfBox) => {
		setPlacementsByKey((prev) => new Map(prev).set(key, { pageNum, pdfBox }));
	}, []);

	const onPlaceAtDefault = useCallback(
		(key: string, pageNum: number) => {
			setPlacementsByKey((prev) => {
				// De-stack, so two slots dropped on one page do not land on top of each other.
				const offset = prev.size * 20;
				return new Map(prev).set(key, {
					pageNum,
					pdfBox: defaultBoxFor(showStampMetadata, offset),
				});
			});
			setSelectedSlotKey(key);
		},
		[showStampMetadata]
	);

	const onRemove = useCallback((key: string) => {
		setPlacementsByKey((prev) => {
			if (!prev.has(key)) {
				return prev;
			}
			const next = new Map(prev);
			next.delete(key);
			return next;
		});
		setSelectedSlotKey((current) => (current === key ? null : current));
	}, []);

	// The hook outlives the modal it serves — the modal is mounted on eligibility, not on `isOpen` —
	// so boxes drawn for one counterparty would otherwise be submitted verbatim for the next.
	const reset = useCallback(() => {
		setPlacementsByKey(new Map());
		setSelectedSlotKey(null);
	}, []);

	const isComplete = useMemo(
		() => isPlacementSetComplete(slots, (slot) => slot.key, placementsByKey),
		[slots, placementsByKey]
	);

	const hasOverlappingPlacements = useMemo(
		() => findOverlappingKeys(placementsByKey, fixedPlacements).size > 0,
		[placementsByKey, fixedPlacements]
	);

	const placements = useMemo(
		() => toPlacementPayload(slots, placementsByKey),
		[slots, placementsByKey]
	);

	return {
		slots,
		placementsByKey,
		slotByKey,
		selectedSlotKey,
		setSelectedSlotKey,
		onPlacementChange,
		onPlaceAtDefault,
		onRemove,
		reset,
		isComplete,
		hasOverlappingPlacements,
		placements,
	};
};
